import { Component, OnInit, OnDestroy, HostListener, ElementRef, inject } from '@angular/core';
import { registerConfirmSteps, registerScreenActions, ScreenPanelsService, SettingsOverlay } from '../../studio-nav/screen-panels.service';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';
import { SETTINGS_PRESS_COMMITS, settingsConfirmSteps, settingsFastActions, settingsHolds } from '../../services/mcp/actions/settings-actions';
import { Router, NavigationEnd } from '@angular/router';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Auth, user } from '@angular/fire/auth';
import { AuthService } from '../../services/auth.service';
import { ClientService } from '../../services/client.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { ApkBuild, MembersService } from '../../services/members.service';
import { progressView, ApkProgress, ApkProgressView } from './apk-progress.util';
import { liveApkLabel, liveApkBusy } from '../../services/live-apk.util';
import { AppleAccount, IosBuild, IosLastAutoBuild, IosPendingRebuild, iosBuildLabel, iosBuildNote, iosBuildBusy as iosBuildBusyRule, appleStep, appleReviewNotPushed, canBuildIphone as canBuildIphoneRule, iphonesOn as iphonesOnRule, iosPendingRebuildNote, iosLastAutoBuildNote, iosZavoRebuildNote, appStoreNotSubmittedLine, APP_STORE_CONNECT_APPS_URL, IosNudge, IosNudgeSwitch, nudgeText, nudgeShown, nudgeSwitchLabel, withNudgeNote, rebuildConfirmReady, IPHONE_GUIDE_URL, IPHONE_GUIDE_LINK_TEXT, viewerTestflightLink } from '../../services/ios-app.util';
import { DeveloperMode, developerModeLabel, isDevEditorHost } from '../../services/dev-editor.util';
import { TestLoginView, rotatesInLabel } from '../../services/test-logins.util';
import { UNPUBLISH_CONFIRM, UNPUBLISHED_NOTE, unpublishOutcome } from '../../services/unpublish.util';
import { DEFAULT_PROJECT_TIME_ZONE, projectTimeZone as currentProjectTimeZone, searchTimeZones } from '../../shared/time/project-clock';
import { accessLevelFor } from '../../services/access-level-cache.util';
import { SystemBarsStateService } from '../../shared/system-bars/system-bars-state.service';
import { WorksOfflineSettingService } from '../../services/works-offline-setting.service';
import { normaliseColour, pagesWithOwnBarColours, resolveSystemBars, withBarColour, type SystemBar } from '../../shared/system-bars/system-bars.util';
import { PageService } from '../../services/page.service';
import {
  LIMITS as LISTING_LIMITS, CATEGORY_LABELS, SCREENSHOT_SIZE_MESSAGE, isScreenshotSize, readImageSize,
  // Before the two choices:
  // clientListingErrors, submissionLabel as submissionLabelRule, parseCountries,
  clientListingErrors, submissionLabel as submissionLabelRule, COUNTRY_CHOICES, countryChoice,
  COUNTRIES_LOCKED_MESSAGE, countriesPricingUrl as countriesPricingUrlRule, AGE_RATING_LABEL, AGE_RATING_HINT,
  NEXT_SUBMISSION_NOTE, editsWaitForNextSubmission as editsWaitForNextSubmissionRule,
  REJECTED_PAUSE, rejectedPauseShown, REJECTION_NOTES_LABEL, REJECTION_NOTES_MAX, rejectionNotesInput,
  savedRejectionNotes, rejectionNotesHeading,
  privacyChoiceOf, privacyChoiceToSend, privacyLinkError, ownPrivacyLink, shouldDraftPrivacy,
  type AppStorePrivacyChoice, type AppStorePrivacyChoiceInput,
  EU_DECLARATION_NOTICE, EU_DECLARATION_URL, EU_SAVE_THEN_CHECK_NOTE, EU_CHECK_SAVE_FIRST, euStatusView, leavingEuLocked, countryOptionLocked, euReadOnlyCheckOffered, euLeftEuStatusShown,
  euOnDespiteChoiceWarning, TRADER_STATUS_LABEL, TRADER_STATUS_HINT,
  type CountryChoice, type EuStatusView,
  type AppStoreListingForm, type AppStoreListingStatus, type AppStoreChecklistItem,
} from '../../services/app-store-listing.util';
import { AppStoreNameCheck, NAME_ON_APP_STORE_WARNING } from '../../services/app-store-name-check.util';
import { PROJECT_KEY, StudioNavService } from '../../studio-nav/studio-nav.service';
import { screenKindOfNavRoute, screenTone } from '../../studio-nav/screen-tone';
import { getZavoBridge } from '../../services/mcp/zavo-bridge';

/** True when both lists hold the same screenshot paths in the same order. */
function samePaths(a: { path: string }[] | undefined, b: { path: string }[] | undefined): boolean {
  const x = a ?? [], y = b ?? [];
  return x.length === y.length && x.every((s, i) => s.path === y[i].path);
}

// Before the two choices, when countries were a mode and typed codes:
// /** The App Store listing form as the card edits it: countries split into a mode + the typed codes, "None" as ''. */
// interface ListingFormView extends Omit<AppStoreListingForm, 'countries' | 'secondaryCategory'> {
//   secondaryCategory: string;
//   countriesMode: 'ALL' | 'SOME';
//   countriesText: string;
// }
/** The App Store listing form as the card edits it: "None" as ''. */
interface ListingFormView extends Omit<AppStoreListingForm, 'secondaryCategory'> {
  secondaryCategory: string;
}

@Component({
    selector: 'app-left-nav',
    imports: [NgClass, NgFor, NgIf],
    templateUrl: './left-nav.component.html',
    styleUrl: './left-nav.component.scss'
})
export class LeftNavComponent implements OnInit, OnDestroy {
  private pulseTimer: any;
  currentRoute: string = 'entities';
  userName: string = 'Loading...';
  userEmail: string = '';
  userRole: string = 'User';
  userInitials: string = '--';
  appVersion: string = '1.0.0';
  environment: string = 'Development';
  showUserDropdown: boolean = false;
  selectedClientId: string = '';

  /**
   * Gates the "Users & workspaces" nav item — a viewer must never see this link at all (the page it
   * leads to is owner/editor only). Deliberately NOT `canEditProject`/`isProjectOwner`: those two are
   * set inside `openSettings()`, behind "has the builder opened settings at least once" — before that
   * they are both false, which would hide the item from an owner who hasn't opened settings yet and
   * then pop it into existence later. This is fetched eagerly here instead, and shared (via
   * `accessLevelFor`) with `UsersWorkspacesComponent`'s own gate so the same project doesn't fetch
   * the access level twice on one visit.
   */
  canSeeUsersWorkspaces: boolean = false;
  private usersWorkspacesAccessSub?: Subscription;
  /** Nav 7b — the note left by `go()` for "open settings" (spec Q1). */
  private navSub?: Subscription;
  private nav = inject(StudioNavService);
  private screenPanels = inject(ScreenPanelsService);
  private settingsCard: string | null = null;
  private waits = inject(ConfirmWaitsNgService);
  private settingsActions = registerScreenActions(settingsFastActions(this));
  private settingsSteps = registerConfirmSteps(settingsConfirmSteps(this));
  // Was (2026-10-30), when only Settings brought its button on screen. The highlight overlay now does it for every screen:
  // revealCommitButton(label: string): void {
  //   setTimeout(() => (this.el.nativeElement as HTMLElement).querySelector(`[data-mcp-button="${label}"]`)?.scrollIntoView({ block: 'center' }), 50);
  // }

  private settingsOverlay: SettingsOverlay = {
    screen: 'settings',
    isOpen: () => this.showSettings,
    close: () => this.closeSettings(),
    here: () => (this.settingsCard ? { panel: this.settingsCard } : {}),
    holds: () => settingsHolds(this, {
      builderCode: this.builderCode,
      tenants: this.tenants,
      publishVersion: this.publishVersion,
      versions: this.versions,
      mobileAppName: this.mobileAppName,
      publishNote: this.publishNote,
      unpublishName: this.unpublishName,
      rebuildName: this.rebuildName,
      rebuildNote: this.rebuildNote,
      // Was (2026-10-30), when a prefill read as if it were already saved: timeZone: this.timeZonePending ?? this.projectTimeZone,
      timeZone: this.projectTimeZone,
      timeZonePending: this.timeZonePending,
      rejectionNotes: this.rejectionNotesText,
      privacyText: this.privacyText,
      ...this.reviewForm,
      ...this.listingForm,
      listingName: this.listingForm.name,
      teamId: this.appleForm.teamId,
      issuerId: this.appleForm.issuerId,
      keyId: this.appleForm.keyId,
      p8: this.appleForm.p8,
      runtimeLoginEmail: this.runtimeLoginEmail,
      runtimeLoginPassword: this.runtimeLoginPassword,
      runtimeNewPassword: this.runtimeNewPassword,
      testLogins: this.testLogins,
    }),
    unsaved: () => (this.settingsFormInUse() ? { count: 1, name: this.settingsFormInUse() as string } : null),
    loading: () => this.settingsReads > 0 || this.settingsLoading || this.testLoginsLoading || this.iphoneLoading,
    // The same conditions as the Live login card in the template.
    whyNot: (card) => {
      if (card !== 'live-login' || this.settingsLoading) return null;
      if (!this.isProjectOwner) return 'only the project owner has the Live login card.';
      return this.publishUrl ? null : 'this project isn\'t published yet, and the Live login card comes with publishing.';
    },
    // Most cards wait on the access level, and some are only for an owner, so a card may not be there to show.
    select: (card) => {
      if (!this.showSettings) return false;
      const element = (this.el.nativeElement as HTMLElement).querySelector(`[data-settings-card="${card}"]`);
      if (!element) return false;
      element.scrollIntoView({ block: 'center' });
      this.settingsCard = card;
      // The card shows versions only behind a click, so Claude's read would otherwise have nothing to give.
      if (card === 'publish' && this.publishUrl) void this.loadVersions();
      return true;
    },
  };

  /**
   * Gates the Bastion item while Bastion is open to the Zavo team only (the server refuses every other
   * account too). False until the claim has been read, so the entry never flashes for an account that
   * cannot use it.
   */
  canSeeBastion: boolean = false;

  /** Only the Zavo desktop app can connect Claude; its bridge opens the app's own panel, which holds the token. */
  readonly canConnectClaude = typeof getZavoBridge()?.openConnectPanel === 'function';

  // Profile / settings overlay (builder code, more later)
  showSettings: boolean = false;
  builderCode: string = '';
  builderName: string = '';   // account name (whatever the builder registered), shown by the code
  builderEmail: string = '';  // account email, shown by the code
  codeLoading: boolean = false;
  codeCopied: boolean = false;

  private auth = inject(Auth);

  // Tenant picker (OWNER-only — Track D builder tenant-scoping). Editors/viewers are locked to
  // the Default (Editor) tenant and never see this section.
  isProjectOwner: boolean = false;
  /** A project viewer: Settings shows them the TestFlight link and nothing else of the iPhone app (Plan 7). */
  isProjectViewer: boolean = false;
  tenants: { id: string; name: string; status: string }[] = [];
  tenantsLoading: boolean = false;

  /**
   * True while the access level is still resolving. Every card except the builder code is gated on
   * `isProjectOwner` / `canEditProject`, which start false — so without this the panel renders one
   * card, sits there, then pops the rest in together. `tenantsLoading` could not cover it: it lives
   * INSIDE the owner-gated tenant card, so on a first open that card does not exist yet and its
   * "Loading tenants…" line is unreachable.
   */
  settingsLoading: boolean = false;
  /** Skeleton placeholders shown while `settingsLoading`. A field, not an inline literal in the
   *  template — a `[1,2,3]` in the binding is a new array every change-detection cycle. */
  readonly settingsSkeletons: number[] = [0, 1, 2];
  trackByIndex = (i: number) => i;

  // Publish / hosting (OWNER-only). publishUrl = the project's permanent public URL once published.
  publishUrl: string | null = null;
  publishVersion: string | null = null;   // the live published version (e.g. "1.2.0")
  publishNote: string = '';
  publishLoading: boolean = false;
  publishError: string = '';
  publishCopied: boolean = false;

  // Development block — visible to any EDITOR, not just the owner. The dev URL is a working tool:
  // an editor can already read and change the content it renders, so hiding where that content
  // runs helps nobody.

  /** The project's Live Android app (release Plan 6): one per project, owner only. */
  liveApk: ApkBuild | null = null;
  liveApkLabel = liveApkLabel;
  private liveApkPoll: any = null;

  // Unpublish (release Plan 5), owner only like the rest of the Publish card.
  unpublishConfirm = UNPUBLISH_CONFIRM;
  unpublishOpen = false;
  unpublishing = false;
  unpublishNeedsName = false;
  unpublishName = '';
  unpublishError = '';

  canEditProject: boolean = false;
  devUrl: string | null = null;
  developerMode: DeveloperMode | null = null;
  developerModeSaving = false;
  developerModeError = '';
  /** The toggle shows only in the Dev editor, and only to the Zavo team. */
  canSetDeveloperMode = false;
  developerModeLabel = developerModeLabel;
  testLogins: TestLoginView[] = [];
  /** The list came back empty because the read failed, so it says nothing about whether any exist. */
  testLoginsLoadFailed = false;
  testLoginsLoading = false;
  rotatingRole = '';
  copiedLogin = '';
  testLoginError = '';
  rotatesInLabel = rotatesInLabel;
  devCopied: boolean = false;
  devApk: { status: string; versionCode?: number; buildId?: string; error?: string | null; appName?: string; progress?: ApkProgress | null } | null = null;
  devApkView: ApkProgressView | null = null;
  // ---- Mobile app identity ----
  mobileAppName = '';
  mobileIconSaving = false;
  mobileIconError: string | null = null;
  mobileIconSaved = false;
  mobileIconUrl: string | null = null;
  // ---- App bar colours (src/app/shared/system-bars/CONTEXT.md) ----
  systemBars = inject(SystemBarsStateService);
  /** A failed app bar colour save, shown under the pickers — the same inline pattern as `mobileIconError` (final review I5). */
  appBarError: string | null = null;
  private pageService = inject(PageService);
  /** Mobile pages that set their own bar colours — named under the app colours so a change that "did nothing" to them is explained. */
  pagesWithOwnBars: { id: string; name: string }[] = [];
  // ---- Works offline (CONTEXT.md) ----
  worksOffline = inject(WorksOfflineSettingService);
  worksOfflineError: string | null = null;
  // ---- Project time zone — every EDITOR sees it; only the OWNER changes it (server-enforced) ----
  projectTimeZone = DEFAULT_PROJECT_TIME_ZONE;
  timeZoneQuery = '';
  timeZonePending: string | null = null;
  timeZoneSaving = false;
  timeZoneError = '';
  get timeZoneMatches(): string[] { return searchTimeZones(this.timeZoneQuery, 30); }

  pickTimeZone(zone: string): void {
    if (!this.isProjectOwner || zone === this.projectTimeZone) return;
    this.timeZonePending = zone;
    this.timeZoneError = '';
  }

  cancelTimeZone(): void {
    this.timeZonePending = null;
    // A prefill's zone is in the box as a search term, and would stay there narrowing the list.
    this.timeZoneQuery = '';
    this.waits.report('set-time-zone', { outcome: 'discarded' });
  }

  async confirmTimeZone(): Promise<void> {
    if (!this.timeZonePending) return;
    this.timeZoneSaving = true;
    this.waits.saving('set-time-zone');
    const project = this.settingsProjectId();
    const r = await this.membersService.setProjectTimeZone(this.timeZonePending);
    this.timeZoneSaving = false;
    if (this.settingsProjectId() !== project) return;
    if (r.ok) {
      this.projectTimeZone = this.timeZonePending;
      this.timeZonePending = null;
      this.timeZoneQuery = '';
      this.waits.report('set-time-zone', { outcome: 'saved', saved: { timeZone: this.projectTimeZone } });
    } else {
      this.timeZoneError = r.error || 'Could not change the time zone';
      this.waits.report('set-time-zone', { outcome: 'failed', reason: this.timeZoneError });
    }
  }
  /** The highest percentage shown so far — passed back in as the floor so the bar cannot retreat. */
  private devApkFloor = 0;
  private devApkTicker: any = null;
  /** When Build was pressed, so the bar can move before the first poll answers. */
  private devApkStartedAt = 0;
  private devApkPollMs = 3000;
  /** Bumped whenever a watch starts. A poll from a superseded watch is discarded — clearInterval
   *  stops future ticks but cannot cancel a request already in flight. */
  private devApkEpoch = 0;
  private devApkPoll: any = null;
  // Versions / revert
  versionsOpen: boolean = false;
  versionsLoading: boolean = false;
  versions: { version: string; kind: 'major' | 'minor'; createdAt?: any }[] = [];
  reverting: string = '';
  // Owner's RUNTIME test login (OWNER-only) — email + a unique-per-project password for logging
  // into the PUBLISHED app as their own (low-privilege) identity. Fetched lazily on Reveal so we
  // don't generate/store a password just by opening settings.
  runtimeLoginEmail: string = '';
  runtimeLoginPassword: string = '';
  runtimeLoginShown: boolean = false;
  runtimeLoginLoading: boolean = false;
  runtimeLoginError: string = '';
  runtimeLoginCopied: boolean = false;
  runtimeNewPassword: string = '';
  /** What Claude put in the new-password box. Apart from `runtimeNewPassword`, which the click sets, so a builder's typing never binds back. */
  runtimePasswordPrefill: string = '';
  get selectedTenantId(): string | null { return this.clientService.selectedTenantId; }
  get selectedClientName(): string {
    return this.clientService.selectedClient?.name || '';
  }

  get lightNav(): boolean {
    // 'usersworkspaces' follows the same pageThemeDark flag as the other three (users-workspaces.component.ts),
    // so on a dark Users & workspaces page the nav must go light too or its badge disappears into the page.
    // Was (2026-10-15): return (this.currentRoute === 'pages' || this.currentRoute === 'entities' || this.currentRoute === 'userandroles' || this.currentRoute === 'bastion' || this.currentRoute === 'usersworkspaces') && this.globalVariablesService.pageThemeDark;
    // The nav goes white only on a page that is dark. Bastion is always white now, so there it keeps the dark nav it has
    // on every light page, whatever the switch says; the rule is shared with the Studio bar (studio-nav/screen-tone.ts).
    return screenTone(screenKindOfNavRoute(this.currentRoute), this.globalVariablesService.pageThemeDark) === 'dark';
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private clientService: ClientService,
    private el: ElementRef,
    private globalVariablesService: GlobalVariablesService,
    private membersService: MembersService,
  ) {}

  // ---- Profile / settings overlay ----
  async openSettings(): Promise<void> {
    this.settingsReads++;
    try {
      await this.readSettings();
    } finally {
      this.settingsReads--;
    }
  }

  /** How many opens are still reading, since the flags below only cover parts of one and leave gaps between them. */
  private settingsReads = 0;

  private async readSettings(): Promise<void> {
    this.showSettings = true;
    this.codeCopied = false;
    // The nav outlives a switch in place: each await below can come back to a different project.
    const project = this.settingsProjectId();

    const needAccount = !this.builderCode;
    // Tenant picker — owner only (listTenants is owner-gated server-side too).
    const needAccess = !this.tenants.length;
    if (needAccount) this.codeLoading = true;
    if (needAccess) { this.tenantsLoading = true; this.settingsLoading = true; }

    // Wave 1 — none of these depend on each other, so they go out together. They used to run as a
    // six-deep await chain, and because EVERY card except the builder code is gated on the access
    // level, that made the whole panel sit empty and then flash in at once.
    const [acc, access, appName, devApk, iconUrl] = await Promise.all([
      needAccount ? this.membersService.getMyAccount() : Promise.resolve(null),
      needAccess ? this.membersService.myAccessLevel() : Promise.resolve(null),
      // Unconditional: a build survives the panel closing, so a reopen must adopt one in flight.
      this.membersService.getMobileAppName(),
      this.membersService.getDevApkBuild(),
      // Likewise the icon. Without this the card only ever showed one that had just been uploaded,
      // and a reopen said "your app will use the default Zavo icon" for a project that had one —
      // which reads as the upload having silently failed.
      this.membersService.getAppIconUrl(),
    ]);

    if (needAccount) {
      this.builderCode = acc?.secretCode || '';
      this.builderName = acc?.name || '';
      this.builderEmail = acc?.email || this.userEmail || '';
      this.codeLoading = false;
    }
    if (this.settingsProjectId() !== project) return;

    if (needAccess) {
      this.isProjectOwner = access === 'owner';
      this.canEditProject = access === 'owner' || access === 'editor';
      this.isProjectViewer = access === 'viewer';
      // Wave 2 — these need the access level, but not each other.
      // enableHosting's `status` already allows viewer server-side; this client gate was the only
      // thing keeping editors from seeing where their own work runs.
      const [tenants, hosting] = await Promise.all([
        this.isProjectOwner ? this.membersService.listTenants() : Promise.resolve([]),
        this.canEditProject ? this.membersService.getHosting() : Promise.resolve(null),
      ]);
      if (this.settingsProjectId() !== project) return;
      this.tenants = tenants;
      if (hosting) {
        this.publishUrl = hosting.url;
        this.publishVersion = hosting.version;
        this.devUrl = hosting.devUrl;
        this.developerMode = hosting.developerMode ?? null;
      }
      this.tenantsLoading = false;
      this.settingsLoading = false;
      this.canSetDeveloperMode = isDevEditorHost(location.hostname) &&
        (await this.authService.hasZavoTeamClaim().catch(() => false));
      if (this.settingsProjectId() !== project) return;
    }

    // Outside the needAccess guard, deliberately: publishUrl/devUrl/isProjectOwner/canEditProject
    // are cached from the first open, but a build or a rotated login is not — this component is
    // never destroyed, so without a fetch on every open, a reopened panel would show whatever the
    // Live app / test-login state was the FIRST time settings were opened this session.
    if (this.canEditProject && this.devUrl) {
      this.testLoginsLoading = true;
      const testLogins = await this.membersService.listTestLogins().catch(() => null);
      if (this.settingsProjectId() !== project) return;
      this.testLogins = testLogins ?? [];
      this.testLoginsLoadFailed = testLogins === null;
      this.testLoginsLoading = false;
    }

    if (this.isProjectOwner && this.publishUrl) {
      const liveApk = await this.membersService.getLiveApkBuild().catch(() => null);
      if (this.settingsProjectId() !== project) return;
      this.liveApk = liveApk;
      if (liveApkBusy(this.liveApk)) this.startLiveApkPoll();
    }
    // Every open, like the Live APK above: a build moves through Apple while the panel is closed.
    if (this.canEditProject) void this.loadIphone();
    else if (this.isProjectViewer) void this.loadViewerIphone();

    this.mobileAppName = appName || '';
    this.mobileIconUrl = iconUrl;
    void this.systemBars.load();
    void this.loadPagesWithOwnBars();
    void this.worksOffline.load();
    this.projectTimeZone = currentProjectTimeZone();
    this.devApk = devApk;
    this.devApkFloor = 0;
    if (this.devApk?.status === 'queued' || this.devApk?.status === 'running') {
      this.startDevApkWatch();
    }
  }

  settingsProjectId(): string {
    return this.clientService.selectedClient?.id || this.clientService.selectedClientId || '';
  }

  /** Everything Settings read about the project just left. An empty `tenants` also makes the next open read afresh. */
  private forgetProjectSettings(): void {
    this.stopLiveApkPoll(); this.stopIosPoll(); this.stopListingPoll(); this.stopDevApkWatch();
    this.isProjectOwner = false;
    this.canEditProject = false;
    this.isProjectViewer = false;
    this.canSetDeveloperMode = false;
    this.tenants = [];
    this.tenantsLoading = false;
    this.settingsLoading = false;
    this.publishUrl = null;
    this.publishVersion = null;
    this.devUrl = null;
    this.developerMode = null;
    this.testLogins = [];
    this.testLoginsLoadFailed = false;
    this.testLoginsLoading = false;
    this.liveApk = null;
    this.viewerIosLink = null;
    this.mobileAppName = '';
    this.mobileIconUrl = null;
    this.runtimeLoginEmail = '';
    this.runtimeLoginPassword = '';
    this.runtimeLoginShown = false;
    this.runtimeLoginError = '';
    this.runtimePasswordPrefill = '';
    this.settingsCard = null;
    this.closeProjectForms();
    for (const commit of SETTINGS_PRESS_COMMITS) this.waits.report(commit, { outcome: 'closed', because: 'the builder moved to another project' });
  }

  /**
   * The nav outlives a switch, so a form left open here would act on the NEXT project: an Unpublish form's button
   * unpublishes whichever project is open when it's pressed. One whose press is already on its way isn't discarded:
   * it may well have reached the server, so it ends as closed below.
   */
  private closeProjectForms(): void {
    if (this.unpublishOpen && !this.unpublishing) this.waits.report('unpublish', { outcome: 'discarded' });
    if (this.rebuildOpen && !this.rebuilding) this.waits.report('rebuild', { outcome: 'discarded' });
    if (this.timeZonePending && !this.timeZoneSaving) this.waits.report('set-time-zone', { outcome: 'discarded' });
    this.unpublishOpen = false; this.unpublishNeedsName = false; this.unpublishName = ''; this.unpublishError = '';
    this.rebuildOpen = false; this.rebuildName = ''; this.rebuildNote = ''; this.rebuildError = ''; this.rebuildStarted = '';
    this.timeZonePending = null; this.timeZoneQuery = ''; this.timeZoneError = '';
    this.appleForm = { teamId: '', issuerId: '', keyId: '', p8: '' }; this.appleP8Name = '';
    this.runtimeNewPassword = '';
    this.reviewForm = { contactFirstName: '', contactLastName: '', contactEmail: '', contactPhone: '', demoUsername: '', demoPassword: '', notes: '' };
    this.publishNote = ''; this.publishError = '';
    this.versions = []; this.versionsOpen = false; this.versionsLoading = false; this.versionsReadFor = null;
    // Only an editor's open reads these again, and read_screen reports them to anyone.
    this.appleAccount = null; this.iphoneAccountRead = false; this.iosBuild = null; this.iosPublicLink = null; this.iosPublicLinkEnabled = true; this.iosStatusLoaded = false;
    this.resetListing();
  }

  /** OWNER-only: publish a new version → permanent public URL (cert provisions ~1-2 min on first). */
  async publish(): Promise<void> {
    if (this.publishLoading) return;
    this.publishLoading = true;
    this.publishError = '';
    this.publishNote = '';
    const project = this.settingsProjectId();
    const r = await this.membersService.publishProject();
    // The answer is about the project that was open when it was pressed; the next one's card isn't its to change.
    if (this.settingsProjectId() !== project) { this.publishLoading = false; return; }
    if (r.ok) {
      this.publishUrl = r.url || this.publishUrl;
      this.publishVersion = r.version || this.publishVersion;
      this.publishNote = [r.note, r.iphone].filter(Boolean).join(' ');
      if (this.versionsOpen) await this.loadVersions();
      if (this.canEditProject) await this.loadIphone();
      // From day 30 the publish repeats the App Store reminder (the ladder as the reload above left it).
      this.publishNote = withNudgeNote(this.publishNote, this.iosNudge);
    } else this.publishError = r.error || 'Publish failed';
    this.publishLoading = false;
  }

  openUnpublish(): void {
    this.unpublishOpen = true;
    this.unpublishNeedsName = false;
    this.unpublishName = '';
    this.unpublishError = '';
  }

  cancelUnpublish(): void {
    if (this.unpublishing) return;
    this.unpublishOpen = false;
    this.waits.report('unpublish', { outcome: 'discarded' });
  }

  async confirmUnpublish(): Promise<void> {
    if (this.unpublishing) return;
    this.unpublishing = true;
    this.unpublishError = '';
    this.waits.saving('unpublish');
    const project = this.settingsProjectId();
    const r = await this.membersService.unpublishProject(this.unpublishNeedsName ? this.unpublishName.trim() : undefined);
    if (this.settingsProjectId() !== project) { this.unpublishing = false; return; }
    const outcome = unpublishOutcome(r);
    if (outcome === 'done') {
      this.publishUrl = null;
      this.publishVersion = null;
      this.versionsOpen = false;
      this.unpublishOpen = false;
      this.publishNote = UNPUBLISHED_NOTE;
      this.liveApk = null;
      this.stopLiveApkPoll();
      this.stopIosPoll();
      this.stopListingPoll();
      this.waits.report('unpublish', { outcome: 'saved', saved: { unpublished: true } });
    } else {
      if (outcome === 'needs-name') this.unpublishNeedsName = true;
      this.unpublishError = r.error || 'Unpublish failed';
      this.waits.report('unpublish', { outcome: 'failed', reason: this.unpublishError });
    }
    this.unpublishing = false;
  }

  async rotateTestLogin(roleId: string): Promise<void> {
    if (this.rotatingRole) return;
    this.rotatingRole = roleId;
    this.testLoginError = '';
    const project = this.settingsProjectId();
    const next = await this.membersService.rotateTestLogin(roleId);
    if (this.settingsProjectId() !== project) { this.rotatingRole = ''; return; }
    if (next) this.testLogins = this.testLogins.map((t) => (t.roleId === roleId ? next : t));
    else this.testLoginError = 'Could not rotate that login — try again.';
    this.rotatingRole = '';
  }

  copyTestLogin(t: TestLoginView): void {
    navigator.clipboard?.writeText(`${t.email}\n${t.password}`).catch(() => undefined);
    this.copiedLogin = t.roleId;
    setTimeout(() => { if (this.copiedLogin === t.roleId) this.copiedLogin = ''; }, 1500);
  }

  async copyDevUrl(): Promise<void> {
    if (!this.devUrl) return;
    await navigator.clipboard.writeText(this.devUrl);
    this.devCopied = true;
    setTimeout(() => (this.devCopied = false), 1500);
  }

  async toggleDeveloperMode(): Promise<void> {
    if (this.developerModeSaving || !this.canSetDeveloperMode) return;
    this.developerModeSaving = true;
    this.developerModeError = '';
    const next = !(this.developerMode?.on);
    const saved = await this.membersService.setDeveloperMode(next);
    if (saved) this.developerMode = saved;
    else this.developerModeError = 'Could not change Developer mode. Try again.';
    this.developerModeSaving = false;
  }

  devApkDownloadUrl(): string {
    return this.membersService.apkDownloadUrl('dev');
  }

  async buildDevApk(): Promise<void> {
    this.devApk = { status: 'queued' };
    this.devApkFloor = 0;
    this.devApkView = null;
    this.devApkStartedAt = Date.now();
    this.devApkPollMs = 3000;
    const r = await this.membersService.startDevApkBuild();
    if (!r.ok) { this.devApk = { status: 'failed' }; return; }
    this.startDevApkWatch();
  }

  /** Whole-number percentage for the label. Rounded here rather than via the `number` pipe, which
   *  would mean pulling DecimalPipe in for one value. */
  devApkPercent(): number { return Math.round(this.devApkView?.percent || 0); }

  /**
   * Has the app name moved on since the APK was built?
   *
   * The label is baked into the APK and no over-the-air update can change it, so a builder who
   * renames their app and then downloads is handed the OLD name with nothing saying why. The build
   * record already stores the label it used, so this is a comparison rather than new state.
   */
  get mobileNameChangedSinceBuild(): boolean {
    const built = this.devApk?.appName;
    if (!built || this.devApk?.status !== 'succeeded') return false;
    const current = (this.mobileAppName || '').trim();
    if (!current) return false;
    return built !== `${current}-preview`;
  }

  trackByKey(_: number, s: { key: string }): string { return s.key; }

  async saveMobileAppName(): Promise<void> {
    await this.membersService.setMobileAppName(this.mobileAppName.trim());
  }

  async onIconPicked(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.mobileIconSaving = true;
    this.mobileIconError = null;
    this.mobileIconSaved = false;
    const r = await this.membersService.uploadAppIcon(file);
    this.mobileIconSaving = false;
    // Cleared either way, so picking the SAME file again after a rejection still fires (change)
    // — otherwise a builder who fixes the file in place appears to have a dead button.
    input.value = '';
    if (!r.ok) { this.mobileIconError = r.error || 'Upload failed.'; return; }
    this.mobileIconSaved = true;
    // Cache-busted: the object is overwritten at a fixed path, so the browser would otherwise show
    // the icon it just replaced and make a successful upload look like it did nothing.
    const url = await this.membersService.getAppIconUrl();
    this.mobileIconUrl = url ? `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}` : null;
  }

  /** The resolved app pair — what an app-coloured page shows. Rules: system-bars.util. */
  get appBars() { return resolveSystemBars(this.systemBars.app(), null); }

  /** True when the app's navigation bar has its own colour rather than following the status bar. */
  get navigationHasOwnColour(): boolean { return !!normaliseColour(this.systemBars.app()?.navigation); }

  get pagesWithOwnBarsText(): string { return this.pagesWithOwnBars.map((p) => p.name).join(', '); }

  async setAppBar(bar: SystemBar, value: string | null): Promise<void> {
    // Was (2026-09-23, final review I5): await this.systemBars.saveApp(withBarColour(this.systemBars.app(), bar, value));
    // ⚠ A FAILED SAVE IS SAID HERE — final review I5. `saveApp` has already put the old colours back; without this
    // catch the rejection was unhandled and the builder saw the picker jump back with no reason given.
    this.appBarError = null;
    try {
      await this.systemBars.saveApp(withBarColour(this.systemBars.app(), bar, value));
    } catch (e) {
      console.error('left-nav: app bar colour save failed', e);
      this.appBarError = `The ${bar === 'status' ? 'status bar' : 'navigation bar'} colour was not saved. Check your connection and try again.`;
    }
  }

  /** The switch moves before its write ends, so the switch alone can't say whether that write held. */
  worksOfflineSaving = false;

  async toggleWorksOffline(): Promise<void> {
    this.worksOfflineError = null;
    this.worksOfflineSaving = true;
    try {
      await this.worksOffline.set(!this.worksOffline.on());
    } catch (e) {
      console.error('left-nav: Works offline save failed', e);
      this.worksOfflineError = 'Works offline was not changed. Check your connection and try again.';
    } finally {
      this.worksOfflineSaving = false;
    }
  }

  private async loadPagesWithOwnBars(): Promise<void> {
    try {
      // Was (2026-09-23, final review M1): this.pagesWithOwnBars = pagesWithOwnBarColours((await this.pageService.query(500)) ?? []);
      // ⚠ flag=false — a read for a count, not a refresh: the default re-emits the shared page list to every
      // subscriber. 1000 matches the page service's own full-list reads.
      this.pagesWithOwnBars = pagesWithOwnBarColours((await this.pageService.query(1000, false)) ?? []);
    } catch (e) {
      console.error('left-nav: could not list pages with their own bar colours', e);
      this.pagesWithOwnBars = [];
    }
  }

  /** Two timers, deliberately. The 15s one asks the server what really happened; the 1s one redraws
   *  the bar from the last answer. Without the second, the bar would move only when the network
   *  replied — four steps in five minutes, which is the stutter this feature exists to remove. */
  private startDevApkWatch(): void {
    this.devApkEpoch++;
    clearInterval(this.devApkPoll);
    clearInterval(this.devApkTicker);
    if (!this.devApkStartedAt) this.devApkStartedAt = Date.now();
    // 3s, not 15s. Progress only exists once a poll has asked CI what happened, so the poll
    // interval is the floor on how soon the bar can move at all — and 15s was chosen when a build
    // took five minutes. A build now finishes in about forty seconds, so a 15s poll meant the bar
    // sat at zero for most of it and then rushed. Backs off after 90s so a stuck build does not
    // poll every three seconds for half an hour.
    this.devApkPoll = setInterval(() => {
      this.pollDevApk();
      if (Date.now() - this.devApkStartedAt > 90_000 && this.devApkPollMs === 3000) {
        this.devApkPollMs = 10_000;
        this.startDevApkWatch();
      }
    }, this.devApkPollMs);
    this.devApkTicker = setInterval(() => this.refreshDevApkView(), 1000);
    this.pollDevApk();
  }

  private stopDevApkWatch(): void {
    clearInterval(this.devApkPoll);
    clearInterval(this.devApkTicker);
    this.devApkView = null;
    this.devApkStartedAt = 0;
    this.devApkPollMs = 3000;
  }

  /** Recompute the bar from the last payload received. */
  private refreshDevApkView(): void {
    const inFlight = this.devApk?.status === 'queued' || this.devApk?.status === 'running';
    const p = this.devApk?.progress;
    if (!inFlight) { this.devApkView = null; return; }
    if (!p) {
      // NO DATA YET — CI has not been asked, or has not started the run. Creep gently to a low
      // ceiling rather than showing a flat zero: the build IS underway, and a bar that does not
      // move reads as a hang. Capped low because this is the one stretch where we genuinely know
      // nothing, and the real phases must have somewhere to go.
      const secs = (Date.now() - (this.devApkStartedAt || Date.now())) / 1000;
      this.devApkView = {
        percent: Math.max(this.devApkFloor, Math.min(12, (secs / 15) * 12)),
        visible: [{ key: 'starting', label: 'Getting things ready', done: false }],
        timeLeftLabel: null,
        overrunning: false,
        stalled: false,
      };
      this.devApkFloor = this.devApkView.percent;
      return;
    }
    this.devApkView = progressView(p, Date.now(), this.devApkFloor);
    this.devApkFloor = this.devApkView.percent;
  }

  /** One poll tick. Split out of buildDevApk so the panel can also resume polling a build that was
   *  already running when it opened — otherwise reopening the panel mid-build shows a dead card. */
  private async pollDevApk(): Promise<void> {
    const epoch = this.devApkEpoch;
    const build = await this.membersService.getDevApkBuild();
    // A request that was already in flight when a NEW build started must not land. Pressing Try
    // again while the previous build's poll was mid-request stamped the old failed record — error
    // message and all — back over the build just started, so the error flashed up and the bar only
    // reappeared on the next tick. clearInterval stops future ticks; it cannot recall this one.
    if (epoch !== this.devApkEpoch) return;
    // A poll that answers with nothing means the REQUEST failed — a network blip, a cold start, or
    // the first poll racing the record being written. It does NOT mean the build disappeared.
    //
    // Assigning it blindly put the Build button back underneath a running build: the template falls
    // through to the button whenever devApk is null, so one dropped poll offered the user a second
    // build of something already building. Keeping the last known state is strictly better — the
    // next poll corrects it 15s later, and a genuinely absent build is only ever discovered on
    // panel open, where there is no in-flight state to protect.
    if (!build && (this.devApk?.status === 'queued' || this.devApk?.status === 'running')) return;
    this.devApk = build;
    this.refreshDevApkView();
    if (this.devApk?.status === 'succeeded' || this.devApk?.status === 'failed') {
      this.stopDevApkWatch();
    }
  }

  copyPublishUrl(): void {
    if (!this.publishUrl) return;
    navigator.clipboard?.writeText(this.publishUrl);
    this.publishCopied = true;
    setTimeout(() => (this.publishCopied = false), 1500);
  }

  // ---- Versions / revert ----
  async toggleVersions(): Promise<void> {
    this.versionsOpen = !this.versionsOpen;
    if (!this.versionsOpen) return;
    if (!this.versions.length) { await this.loadVersions(); return; }
  }

  /** Public: the revert action reads afresh before it checks a version. */
  async loadVersions(): Promise<void> {
    this.versionsLoading = true;
    const project = this.settingsProjectId();
    const r = await this.membersService.listVersions();
    if (this.settingsProjectId() !== project) return;
    this.versions = (r.versions || []).slice().reverse(); // newest first
    this.versionsReadFor = project;
    if (r.current) this.publishVersion = r.current;
    this.versionsLoading = false;
  }

  /** `versions` outlives a project switch, so a read has to know whose they are. */
  private versionsReadFor: string | null = null;
  versionsKnown(): boolean { return this.versionsReadFor !== null && this.versionsReadFor === this.settingsProjectId(); }

  private major(v: string): number { return parseInt((v || '0').split('.')[0], 10) || 0; }

  /** Revertible = same major as the latest version (the live backend schema). Cross-major is locked. */
  canRevert(v: string): boolean {
    if (!this.versions.length) return false;
    const latestMajor = Math.max(...this.versions.map((x) => this.major(x.version)));
    return this.major(v) === latestMajor && v !== this.publishVersion;
  }

  async revert(v: string): Promise<void> {
    if (this.reverting) return;
    this.reverting = v;
    this.publishError = '';
    const project = this.settingsProjectId();
    const r = await this.membersService.revertTo(v);
    if (this.settingsProjectId() !== project) { this.reverting = ''; return; }
    if (r.ok) {
      this.publishVersion = v;
      if (r.iphone) this.publishNote = r.iphone;
      if (this.canEditProject) await this.loadIphone();
    } else this.publishError = r.error || 'Revert failed';
    this.reverting = '';
  }

  liveApkBusy(): boolean { return liveApkBusy(this.liveApk); }

  liveApkDownloadUrl(): string { return this.membersService.apkDownloadUrl('prod'); }

  async buildLiveApk(): Promise<void> {
    if (liveApkBusy(this.liveApk)) return;
    const project = this.settingsProjectId();
    this.liveApk = { ...(this.liveApk || {} as ApkBuild), status: 'queued', error: null } as ApkBuild;
    const r = await this.membersService.startLiveApkBuild();
    if (this.settingsProjectId() !== project) return;
    if (!r.ok) { this.liveApk = { ...(this.liveApk as ApkBuild), status: 'failed', error: r.error || 'Could not start the build' }; return; }
    this.startLiveApkPoll();
  }

  /** One poll while a Live build runs; stops on its own when it settles or settings close. */
  private startLiveApkPoll(): void {
    this.stopLiveApkPoll();
    const project = this.settingsProjectId();
    this.liveApkPoll = setInterval(async () => {
      if (!this.showSettings || this.settingsProjectId() !== project) { this.stopLiveApkPoll(); return; }
      const read = await this.membersService.getLiveApkBuild().catch(() => this.liveApk);
      if (this.settingsProjectId() !== project) return;
      this.liveApk = read;
      if (!liveApkBusy(this.liveApk)) this.stopLiveApkPoll();
    }, 5000);
  }

  private stopLiveApkPoll(): void {
    if (this.liveApkPoll) { clearInterval(this.liveApkPoll); this.liveApkPoll = null; }
  }

  // ---- iPhone app (iPhone apps Plan 3) ----
  // Anyone who edits the project sees the card; every control is owner-only (server-enforced too).
  // The .p8 key and the reviewer's password are write-only: never read back, never shown.
  appleAccount: AppleAccount | null = null;
  appleForm = { teamId: '', issuerId: '', keyId: '', p8: '' };
  appleP8Name = '';
  appleBusy = false;
  appleError: string | null = null;
  reviewForm = { contactFirstName: '', contactLastName: '', contactEmail: '', contactPhone: '', demoUsername: '', demoPassword: '', notes: '' };
  reviewBusy = false;
  reviewError: string | null = null;
  reviewSaved = false;
  iosBuild: IosBuild | null = null;
  iosPublicLink: string | null = null;
  iosPublicLinkEnabled = true;
  iosReconcileError: string | null = null;
  /** The last automatic build start (publish, revert, queued rebuild) and a rebuild queued behind this one. */
  iosLastAutoBuild: IosLastAutoBuild | null = null;
  iosPendingRebuild: IosPendingRebuild | null = null;
  iosBuildError: string | null = null;
  iosLinkCopied = false;
  /** True while startIosBuild is in flight, so a second click cannot start a second build. */
  iosBuilding = false;
  /** True while the TestFlight link switch is in flight; the switch is disabled meanwhile. */
  iosLinkBusy = false;
  /** The account or build status call FAILED (not "none"): the card says so and offers Retry, never the connect form. */
  iphoneLoadFailed = false;
  iphoneLoading = false;
  /** Null `appleAccount` alone can't tell "no account" from "not read yet": the first read starts only after the rest of the open. */
  iphoneAccountRead = false;
  private iphoneLoadRun = 0;
  private iphoneLoadProject: string | null = null;
  /** The owner's "Replace the key" disclosure. null = follow the default: open only while the key needs fixing. */
  replaceKeyOpen: boolean | null = null;
  private iosPoll: any = null;
  /** A poll tick skips its round while the previous status call is still out. */
  private iosPollInFlight = false;
  iosBuildLabel = iosBuildLabel;
  iosBuildNote = iosBuildNote;
  iosPendingRebuildNote = iosPendingRebuildNote;
  iosLastAutoBuildNote = iosLastAutoBuildNote;
  iosZavoRebuildNote = iosZavoRebuildNote;
  appStoreNotSubmittedLine = appStoreNotSubmittedLine;
  appleStep = appleStep;
  appleReviewNotPushed = appleReviewNotPushed;
  appStoreConnectUrl = APP_STORE_CONNECT_APPS_URL;
  /** The builder guide, served by the editor app (never the Live build). Owners and editors see the link. */
  readonly iphoneGuideUrl = IPHONE_GUIDE_URL;
  readonly iphoneGuideLinkText = IPHONE_GUIDE_LINK_TEXT;

  // ---- Viewers (Plan 7): the TestFlight link, and only that ----
  // One buildIos status read per open (the status action allows viewers). Nothing else from it is
  // kept, and no poll starts: a viewer never gets the build, the reminder or the App Store state.
  viewerIosLink: string | null = null;
  viewerIosLinkCopied = false;

  private async loadViewerIphone(): Promise<void> {
    this.viewerIosLink = null;
    const s = await this.membersService.getIosBuild().catch(() => undefined);
    if (this.showSettings && this.isProjectViewer) this.viewerIosLink = viewerTestflightLink(s);
  }

  copyViewerIosLink(): void {
    if (!this.viewerIosLink) return;
    navigator.clipboard?.writeText(this.viewerIosLink);
    this.viewerIosLinkCopied = true;
    setTimeout(() => (this.viewerIosLinkCopied = false), 1500);
  }

  /** Public: the card's Retry calls it after a failed load. */
  async loadIphone(): Promise<void> {
    const project = this.settingsProjectId();
    // A load still running for the project just left must not stop this one's.
    if (this.iphoneLoading && this.iphoneLoadProject === project) return;
    const run = ++this.iphoneLoadRun;
    this.iphoneLoadProject = project;
    this.iphoneLoading = true;
    const superseded = () => run !== this.iphoneLoadRun || this.settingsProjectId() !== project;
    try {
      // Every open starts clean: a message or a build from the last open must not outlive it.
      this.appleError = null; this.iosBuildError = null; this.reviewError = null; this.reviewSaved = false;
      this.iosBuild = null; this.iosPublicLink = null; this.iosReconcileError = null;
      this.iosLastAutoBuild = null; this.iosPendingRebuild = null;
      this.replaceKeyOpen = null; this.iphoneLoadFailed = false;
      this.iosNudgeError = ''; this.rebuildStarted = ''; this.iosStatusLoaded = false;
      this.resetListing();
      const account = await this.membersService.getAppleAccount().catch(() => undefined);
      if (superseded()) return;
      // A failed call is not "no account": never show the connect form on the strength of it.
      if (account === undefined) { this.appleAccount = null; this.iphoneLoadFailed = true; return; }
      this.appleAccount = account;
      this.iphoneAccountRead = true;
      this.seedReviewForm();
      const statusRead = !this.appleAccount?.connected || await this.refreshIosBuild();
      if (superseded()) return;
      if (!statusRead) { this.appleAccount = null; this.iphoneLoadFailed = true; }
      // The App Store section only exists once Apple holds the app record.
      if (this.appleAccount?.connected && this.appleAccount?.appleAppId) await this.loadListing();
    } finally {
      if (run === this.iphoneLoadRun) this.iphoneLoading = false;
    }
  }

  /** The reviewer form from what the server holds — never the password, which is write-only. */
  private seedReviewForm(): void {
    const r = this.appleAccount?.review;
    this.reviewForm = { contactFirstName: '', contactLastName: '', contactEmail: '', contactPhone: '', demoUsername: '', demoPassword: '', notes: '' };
    if (r) this.reviewForm = { ...this.reviewForm, contactFirstName: r.contactFirstName, contactLastName: r.contactLastName, contactEmail: r.contactEmail, contactPhone: r.contactPhone, demoUsername: r.demoUsername, notes: r.notes };
  }

  /** The connect form shows once: as the first step, or — connected — inside "Replace the key". */
  appleKeyFormShown(): boolean {
    if (!this.isProjectOwner || this.iphoneLoadFailed) return false;
    const step = appleStep(this.appleAccount);
    if (step === 'connect') return true;
    return !!this.appleAccount?.connected && (this.replaceKeyOpen ?? step === 'fix-key');
  }

  toggleReplaceKey(): void {
    this.replaceKeyOpen = !this.appleKeyFormShown();
    if (!this.replaceKeyOpen) this.waits.report('connect-apple', { outcome: 'discarded' });
  }

  /** False when the status call failed; the card keeps what it had (a poll tick just tries again). */
  private async refreshIosBuild(): Promise<boolean> {
    const project = this.settingsProjectId();
    const s = await this.membersService.getIosBuild().catch(() => undefined);
    if (this.settingsProjectId() !== project || !s) return false;
    this.iosBuild = s.build; this.iosPublicLink = s.publicLink; this.iosPublicLinkEnabled = s.publicLinkEnabled; this.iosReconcileError = s.reconcileError;
    this.iosLastAutoBuild = s.lastAutoBuild; this.iosPendingRebuild = s.pendingRebuild;
    this.iosNudge = s.nudge; this.iosNudgeSwitch = s.nudgeSwitch; this.iosStatusLoaded = true;
    if (iosBuildBusyRule(this.iosBuild)) this.startIosPoll(); else this.stopIosPoll();
    return true;
  }

  // ---- App Store reminder (iPhone apps Plan 6) ----
  // The ladder from buildIos's status: day 14 an amber strip in Settings and a dot on the Settings
  // icon (owners), day 30 the publish note repeats it, day 60 new publishes stop reaching iPhones.
  iosNudge: IosNudge = { level: 'none', day: null };
  /** Who switched reminders off, and when — buildIos sends it to the Zavo team only. */
  iosNudgeSwitch: IosNudgeSwitch | null = null;
  /** The owner check for the Settings dot, from the eager access level: `isProjectOwner` is only set once settings open. */
  iosNudgeOwner = false;
  nudgeText = nudgeText;
  nudgeShown = nudgeShown;
  nudgeSwitchLabel = nudgeSwitchLabel;
  iosNudgeSaving = false;
  iosNudgeError = '';
  /** The card's own status call has answered for this project: only then may "couldn't read the switch" show. */
  iosStatusLoaded = false;

  /**
   * The Settings dot needs the ladder before settings is ever opened: ONE buildIos status call per
   * project load, owners only — no poll. It answers `none` cheaply for a project with no iPhone
   * build (no Apple call is made then). Opening settings refreshes it with the card's own call.
   */
  private async loadIosNudge(projectId: string, owner: boolean): Promise<void> {
    const current = () => this.clientService.selectedClient?.id || this.clientService.selectedClientId || '';
    // A project switched while the access level was read keeps its own state: touch nothing.
    if (current() !== projectId) return;
    this.iosNudge = { level: 'none', day: null }; this.iosNudgeSwitch = null; this.iosStatusLoaded = false;
    this.iosNudgeOwner = owner;
    if (!owner) return;
    const s = await this.membersService.getIosBuild().catch(() => undefined);
    if (!s || current() !== projectId) return;   // a failed call shows nothing; a project switched meanwhile keeps its own
    this.iosNudge = s.nudge; this.iosNudgeSwitch = s.nudgeSwitch;
  }

  /** Zavo team, Dev editor: App Store reminders off or back on (setIosNudge refuses anyone else). */
  async toggleIosNudge(): Promise<void> {
    if (this.iosNudgeSaving || !this.canSetDeveloperMode || !this.iosNudgeSwitch) return;
    this.iosNudgeSaving = true; this.iosNudgeError = '';
    const project = this.settingsProjectId();
    const r = await this.membersService.setIosNudge(!this.iosNudgeSwitch.off);
    if (this.settingsProjectId() !== project) { this.iosNudgeSaving = false; return; }
    if (r.ok) {
      // The switch as the server now holds it; then the ladder it gives. A status call that couldn't
      // read the switch must not undo what setIosNudge just confirmed.
      if (r.iosNudge) this.iosNudgeSwitch = r.iosNudge;
      const written = this.iosNudgeSwitch;
      await this.refreshIosBuild();
      if (!this.iosNudgeSwitch && written) this.iosNudgeSwitch = written;
    }
    else this.iosNudgeError = r.error || 'Could not change App Store reminders. Try again.';
    this.iosNudgeSaving = false;
  }

  // ---- Zavo-team rebuild (iPhone apps Plan 6): the delete-page modal's type-to-confirm ----
  rebuildOpen = false;
  rebuildName = '';
  rebuildNote = '';
  rebuilding = false;
  rebuildError = '';
  rebuildStarted = '';

  rebuildReady(): boolean { return rebuildConfirmReady(this.selectedClientName, this.rebuildName, this.rebuildNote); }

  openRebuild(): void {
    this.rebuildOpen = true; this.rebuildName = ''; this.rebuildNote = ''; this.rebuildError = ''; this.rebuildStarted = '';
  }

  cancelRebuild(): void {
    if (this.rebuilding) return;
    this.rebuildOpen = false;
    this.waits.report('rebuild', { outcome: 'discarded' });
  }

  async confirmRebuild(): Promise<void> {
    if (this.rebuilding || !this.canSetDeveloperMode || !this.rebuildReady()) return;
    this.rebuilding = true; this.rebuildError = '';
    this.waits.saving('rebuild');
    try {
      const project = this.settingsProjectId();
      const r = await this.membersService.rebuildIosApp(this.rebuildNote.trim());
      if (this.settingsProjectId() !== project) return;
      if (!r.ok) {
        this.rebuildError = r.error || 'Could not rebuild the iPhone app';
        this.waits.report('rebuild', { outcome: 'failed', reason: this.rebuildError });
        return;
      }
      this.rebuildOpen = false;
      this.rebuildStarted = 'Rebuild started.';
      await this.refreshIosBuild();
      this.waits.report('rebuild', { outcome: 'saved', saved: { rebuildStarted: true } });
    } finally {
      this.rebuilding = false;
    }
  }

  /** One poll while an iPhone build is in flight; stops when it settles or settings close. */
  private startIosPoll(): void {
    if (this.iosPoll) return;
    this.iosPoll = setInterval(async () => {
      if (!this.showSettings) { this.stopIosPoll(); return; }
      if (this.iosPollInFlight) return;
      this.iosPollInFlight = true;
      try { await this.refreshIosBuild(); } finally { this.iosPollInFlight = false; }
    }, 15000);
  }

  private stopIosPoll(): void { if (this.iosPoll) { clearInterval(this.iosPoll); this.iosPoll = null; } }

  onP8Picked(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { this.appleForm.p8 = String(reader.result || ''); this.appleP8Name = file.name; };
    reader.readAsText(file);
  }

  async connectApple(): Promise<void> {
    this.appleBusy = true; this.appleError = null;
    this.waits.saving('connect-apple');
    const project = this.settingsProjectId();
    const r = await this.membersService.connectAppleAccount(this.appleForm);
    this.appleBusy = false;
    if (this.settingsProjectId() !== project) return;
    if (!r.ok) {
      this.appleError = r.error || null;
      this.waits.report('connect-apple', { outcome: 'failed', reason: this.appleError ?? 'Apple didn\'t accept the key.' });
      return;
    }
    this.appleAccount = r.account || null;
    this.appleForm = { teamId: '', issuerId: '', keyId: '', p8: '' }; this.appleP8Name = '';
    this.replaceKeyOpen = null;
    // A replaced key keeps the reviewer details server-side; show them again, password left empty.
    this.seedReviewForm();
    await this.refreshIosBuild();
    this.ensureListing();
    this.waits.report('connect-apple', { outcome: 'saved', saved: { connected: !!this.appleAccount?.connected, step: appleStep(this.appleAccount) } });
  }

  async refreshApple(): Promise<void> {
    this.appleBusy = true; this.appleError = null;
    const r = await this.membersService.refreshAppleAccount();
    this.appleBusy = false;
    if (!r.ok) { this.appleError = r.error || null; return; }
    this.appleAccount = r.account || null;
    // "I've created it" is the moment the App Store section first appears.
    this.ensureListing();
  }

  async saveReview(): Promise<void> {
    this.reviewBusy = true; this.reviewError = null; this.reviewSaved = false;
    const project = this.settingsProjectId();
    const r = await this.membersService.saveAppleReview(this.reviewForm);
    this.reviewBusy = false;
    if (this.settingsProjectId() !== project) return;
    if (!r.ok) { this.reviewError = r.error || null; return; }
    this.appleAccount = r.account || this.appleAccount;
    this.reviewForm.demoPassword = '';
    this.reviewSaved = true;
  }

  canBuildIphone(): boolean { return canBuildIphoneRule(this.appleAccount, !!this.publishUrl, iosBuildBusyRule(this.iosBuild)); }
  iosBuildBusy(): boolean { return iosBuildBusyRule(this.iosBuild); }
  /** The published version iPhones are actually running — may lag `publishVersion` until Apple approves. */
  iphonesOn(): string | null { return iphonesOnRule(this.iosBuild, this.listingStatus?.listing?.approved ?? null); }

  async buildIphone(): Promise<void> {
    if (this.iosBuilding || !this.canBuildIphone()) return;
    this.iosBuilding = true;
    this.iosBuildError = null;
    const project = this.settingsProjectId();
    try {
      const r = await this.membersService.startIosBuild();
      if (this.settingsProjectId() !== project) return;
      if (!r.ok) { this.iosBuildError = r.error || 'Could not start the iPhone build'; return; }
      await this.refreshIosBuild();
      if (this.settingsProjectId() !== project) return;
      this.startIosPoll();
    } finally {
      this.iosBuilding = false;
    }
  }

  async toggleIosLink(): Promise<void> {
    if (this.iosLinkBusy) return;
    this.iosLinkBusy = true; this.iosBuildError = null;
    try {
      const project = this.settingsProjectId();
      const r = await this.membersService.setIosPublicLink(!this.iosPublicLinkEnabled);
      if (this.settingsProjectId() !== project) return;
      if (!r) { this.iosBuildError = 'Could not change the TestFlight link. Try again.'; return; }
      // Trust the server's answer, not a local flip.
      this.iosPublicLink = r.publicLink; this.iosPublicLinkEnabled = r.publicLinkEnabled;
    } finally {
      this.iosLinkBusy = false;
    }
  }

  copyIosLink(): void {
    if (!this.iosPublicLink) return;
    navigator.clipboard?.writeText(this.iosPublicLink);
    this.iosLinkCopied = true;
    setTimeout(() => (this.iosLinkCopied = false), 1500);
  }

  // ---- App Store section (iPhone apps Plan 4) ----
  // Inside the iPhone card once Apple holds the app record. Everyone who edits the project sees the
  // listing, checklist and status; only the owner edits, ticks, uploads, approves or submits (the
  // server enforces the same). Every write hands back the full new state, which replaces ours as-is.
  listingStatus: AppStoreListingStatus | null = null;
  /** The status call FAILED (not "nothing saved yet"): the section says so and offers Retry. */
  listingLoadFailed = false;
  listingLoading = false;
  listingForm: ListingFormView = this.blankListingForm();
  /** Client-side problems (every one, before any round trip) or the server's refusal of Save. */
  listingErrors: string[] = [];
  listingSaving = false;
  listingSaved = false;
  readonly listingNameCheck = new AppStoreNameCheck();
  readonly listingNameWarning = NAME_ON_APP_STORE_WARNING;
  shotBusy = false;
  shotError: string | null = null;
  privacyText = '';
  /** The owner reopened an approved policy to change it. */
  privacyEditing = false;
  privacyBusy = false;
  privacyError: string | null = null;
  /** An own link picked with none saved yet: only the card knows until the link is saved. */
  privacyPicked: AppStorePrivacyChoice | null = null;
  privacyLinkText = '';
  /** The website step whose tick is in flight. */
  stepBusy: string | null = null;
  checklistError: string | null = null;
  submitting = false;
  submitError: string | null = null;
  /** Apple's notes on a rejected version (Plan 7): the owner's textarea, and its Save. */
  rejectionNotesText = '';
  rejectionNotesBusy = false;
  rejectionNotesError: string | null = null;
  rejectionNotesSaved = false;
  private listingPoll: any = null;
  /** A poll tick skips its round while the previous status call is still out. */
  private listingPollInFlight = false;
  readonly listingLimits = LISTING_LIMITS;
  readonly listingCategories = Object.entries(CATEGORY_LABELS).map(([id, label]) => ({ id, label }));
  readonly maxScreenshots = 10;
  submissionLabel = submissionLabelRule;
  readonly countriesLockedMessage = COUNTRIES_LOCKED_MESSAGE;
  readonly countryChoices = COUNTRY_CHOICES;
  readonly euDeclarationNotice = EU_DECLARATION_NOTICE;
  readonly euDeclarationUrl = EU_DECLARATION_URL;
  readonly traderStatusLabel = TRADER_STATUS_LABEL;
  readonly traderStatusHint = TRADER_STATUS_HINT;
  readonly euSaveThenCheckNote = EU_SAVE_THEN_CHECK_NOTE;
  readonly euCheckSaveFirst = EU_CHECK_SAVE_FIRST;
  euCheckBusy = false;
  euCheckError: string | null = null;
  readonly ageRatingLabel = AGE_RATING_LABEL;
  readonly ageRatingHint = AGE_RATING_HINT;
  readonly nextSubmissionNote = NEXT_SUBMISSION_NOTE;
  readonly rejectedPause = REJECTED_PAUSE;
  readonly rejectionNotesLabel = REJECTION_NOTES_LABEL;
  readonly rejectionNotesMax = REJECTION_NOTES_MAX;
  rejectedPauseShown = rejectedPauseShown;
  savedRejectionNotes = savedRejectionNotes;
  rejectionNotesHeading = rejectionNotesHeading;

  // Before the card could add the EU after the first push:
  // /** Apple already has the countries: the controls are disabled for everyone (E1). */
  /** Apple already has the countries, so the card can only add the EU. */
  countriesLocked(): boolean { return !!this.listingStatus?.countriesLocked; }
  private savedCountries(): CountryChoice | undefined { return this.listingStatus?.listing?.countries; }
  countryOptionLocked(option: CountryChoice): boolean { return countryOptionLocked(option, this.savedCountries(), this.countriesLocked(), this.listingStatus?.eu); }
  /** The re-check acts on the saved choice, so it's offered only once that choice is the EU and Apple has a list to add to. */
  // Before leaving the EU stayed open until a territory had been switched on, when this also meant locked:
  // euAfterFirstPush(): boolean { return leavingEuLocked(this.savedCountries(), this.countriesLocked()); }
  euAfterFirstPush(): boolean { return this.countriesLocked() && this.savedCountries() === 'WITH_EU'; }
  /** A territory that has been on can only be switched off at Apple, so the card stops offering it. */
  leavingEuLocked(): boolean { return leavingEuLocked(this.savedCountries(), this.countriesLocked(), this.listingStatus?.eu); }
  euOnDespiteChoiceWarning(): string | null { return euOnDespiteChoiceWarning(this.listingStatus?.listing); }
  /** Chosen on the form but not saved, after the first push: the re-check only reads the saved choice. */
  euChosenNotSaved(): boolean {
    return this.countriesLocked() && this.listingForm.countries === 'WITH_EU' && countryChoice(this.savedCountries()) !== 'WITH_EU';
  }
  euReadOnlyCheckOffered(): boolean { return euReadOnlyCheckOffered(this.listingStatus?.listing, this.listingStatus?.eu, this.countriesLocked()); }
  euLeftEuStatusShown(): boolean { return euLeftEuStatusShown(this.listingStatus?.listing, this.listingStatus?.eu, this.countriesLocked()); }
  euCheckOffered(): boolean { return this.euAfterFirstPush() || this.euReadOnlyCheckOffered(); }
  /** The server acts on the saved choice, so a check against an unsaved radio would answer a different question. */
  euCheckNeedsSave(): boolean { return countryChoice(this.listingForm.countries) !== countryChoice(this.savedCountries()); }
  // Before a builder who had left the EU could check it:
  // euView(): EuStatusView { return euStatusView(this.listingStatus?.eu); }
  // Before the button could go while the status stayed:
  // euView(): EuStatusView { return euStatusView(this.listingStatus?.eu, { leftEu: this.euReadOnlyCheckOffered() }); }
  euView(): EuStatusView { return euStatusView(this.listingStatus?.eu, { leftEu: this.euLeftEuStatusShown() }); }
  euToneClass(tone: EuStatusView['tone']): string {
    if (tone === 'ok') return 'text-green-700 dark:text-green-400';
    if (tone === 'warn') return 'text-amber-700 dark:text-amber-400';
    if (tone === 'bad') return 'text-red-600 dark:text-red-400';
    return 'text-gray-500 dark:text-gray-400';
  }

  async checkEuStatus(): Promise<void> {
    if (!this.isProjectOwner || this.euCheckBusy || this.euCheckNeedsSave()) return;
    this.euCheckBusy = true; this.euCheckError = null;
    try {
      const r = await this.membersService.checkEuStatus();
      if (!r.ok) { this.euCheckError = r.error || 'Could not check your EU status.'; return; }
      if (r.state) this.applyListingState(r.state, true);
    } finally {
      this.euCheckBusy = false;
    }
  }

  countriesPricingUrl(): string { return countriesPricingUrlRule(this.appleAccount?.appleAppId); }
  /** A submission exists and didn't fail, so a save waits for the next one (E3). */
  editsWaitForNextSubmission(): boolean { return editsWaitForNextSubmissionRule(this.listingStatus?.submission); }

  private blankListingForm(): ListingFormView {
    return {
      name: '', subtitle: '', description: '', keywords: '', supportUrl: '', marketingUrl: '', promotionalText: '', copyright: '',
      primaryCategory: '', secondaryCategory: '', noObjectionableContent: false, usesThirdPartyContent: false, traderStatusConfirmed: false,
      // Before the two choices:
      // countriesMode: 'ALL', countriesText: '', distribution: 'public',
      countries: countryChoice(null), distribution: 'public',
    };
  }

  /** Every open starts clean, like the rest of the iPhone card. */
  private listingLoadRun = 0;
  private listingLoadProject: string | null = null;

  private resetListing(): void {
    this.stopListingPoll();
    this.listingStatus = null; this.listingLoadFailed = false;
    this.listingForm = this.blankListingForm(); this.listingErrors = []; this.listingSaved = false;
    this.listingNameCheck.clear();
    this.shotError = null; this.privacyText = ''; this.privacyEditing = false; this.privacyError = null;
    this.privacyPicked = null; this.privacyLinkText = '';
    this.checklistError = null; this.submitError = null;
    this.rejectionNotesText = ''; this.rejectionNotesError = null; this.rejectionNotesSaved = false;
    this.euCheckError = null;
  }

  /** Public: the section's Retry calls it after a failed load. */
  async loadListing(): Promise<void> {
    const project = this.settingsProjectId();
    if (this.listingLoading && this.listingLoadProject === project) return;
    const run = ++this.listingLoadRun;
    this.listingLoadProject = project;
    this.listingLoading = true; this.listingLoadFailed = false;
    try {
      const s = await this.membersService.getListing().catch(() => undefined);
      if (run !== this.listingLoadRun || this.settingsProjectId() !== project) return;
      if (!s) { this.listingLoadFailed = true; return; }
      this.applyListingState(s);
      this.seedListingForm();
      this.seedPrivacyText();
      this.seedRejectionNotes();
      // First visit: fill the policy from Zavo's template (owner only — editors never get the draft).
      // Before the builder could use their own link:
      // if (this.isProjectOwner && !s.listing?.privacy?.approvedText && !s.listing?.privacy?.draft) await this.draftPrivacyPolicy(false);
      if (this.isProjectOwner && shouldDraftPrivacy(s.listing)) await this.draftPrivacyPolicy(false);
    } finally {
      if (run === this.listingLoadRun) this.listingLoading = false;
    }
  }

  /** The app record just appeared (connect / "I've created it"): load the section once. */
  private ensureListing(): void {
    if (this.appleAccount?.appleAppId && !this.listingStatus && !this.listingLoading) void this.loadListing();
  }

  /**
   * Takes a server state as the card's truth, and starts or stops the submission poll to match.
   * Deliberately leaves the listing form and the policy text alone: a tick or a poll must not wipe
   * what the owner is typing. Those are re-seeded only by a load or by their own write.
   */
  private applyListingState(s: AppStoreListingStatus, fromWrite = false): void {
    const prev = this.listingStatus;
    // Only `status` reconciles with Apple, so a write's state says nothing about Apple: keep the last word.
    if (fromWrite) s = { ...s, reconcileError: prev?.reconcileError ?? null };
    // Same screenshots in the same order: keep the URLs already loaded, so the thumbnails aren't re-downloaded.
    if (prev && samePaths(prev.listing?.screenshots, s.listing?.screenshots)) s = { ...s, thumbUrls: prev.thumbUrls };
    this.listingStatus = s;
    if (this.listingSubmissionMoving()) this.startListingPoll(); else this.stopListingPoll();
  }

  private listingSubmissionMoving(): boolean {
    const st = this.listingStatus?.submission?.status;
    return st === 'submitted' || st === 'in_review';
  }

  /** One poll while Apple holds a submission; stops when it settles or settings close. */
  private startListingPoll(): void {
    if (this.listingPoll) return;
    this.listingPoll = setInterval(async () => {
      if (!this.showSettings) { this.stopListingPoll(); return; }
      if (this.listingPollInFlight) return;
      this.listingPollInFlight = true;
      try {
        // A failed tick keeps what the card shows; the next one tries again.
        const s = await this.membersService.getListing().catch(() => undefined);
        if (s && this.showSettings) this.applyListingState(s);
      } finally {
        this.listingPollInFlight = false;
      }
    }, 30000);
  }

  private stopListingPoll(): void { if (this.listingPoll) { clearInterval(this.listingPoll); this.listingPoll = null; } }

  private seedListingForm(): void {
    const l = this.listingStatus?.listing;
    const blank = this.blankListingForm();
    // Before a seeded name was checked with Apple:
    // if (!l) { this.listingForm = { ...blank, name: this.mobileAppName || '' }; return; }
    if (!l) { this.listingForm = { ...blank, name: this.mobileAppName || '' }; this.checkSeededListingName(); return; }
    // Replaced by the two choices: no typed list any more.
    // const some = Array.isArray(l.countries);
    this.listingForm = {
      name: l.name ?? (this.mobileAppName || ''),
      subtitle: l.subtitle ?? '',
      description: l.description ?? '',
      keywords: l.keywords ?? '',
      supportUrl: l.supportUrl ?? '',
      marketingUrl: l.marketingUrl ?? '',
      promotionalText: l.promotionalText ?? '',
      copyright: l.copyright ?? '',
      primaryCategory: l.primaryCategory ?? '',
      secondaryCategory: l.secondaryCategory ?? '',
      noObjectionableContent: l.noObjectionableContent === true,
      usesThirdPartyContent: l.usesThirdPartyContent === true,
      traderStatusConfirmed: l.traderStatusConfirmed === true,
      // Replaced by the two choices: no typed list any more.
      // countriesMode: some ? 'SOME' : 'ALL',
      // countriesText: some ? (l.countries as string[]).join(', ') : '',
      countries: countryChoice(l.countries),
      distribution: l.distribution === 'unlisted' ? 'unlisted' : 'public',
    };
    this.checkSeededListingName();
  }

  // Before the countries stopped being a typed list:
  // /** The form as `saveListing` sends it. Unparseable "Only these" text becomes [], which the rules flag. */
  /** The form as `saveListing` sends it. */
  private listingFormValue(): AppStoreListingForm {
    const f = this.listingForm;
    // Replaced by the two choices: no typed list any more.
    // const countries = f.countriesMode === 'ALL' ? 'ALL' : (parseCountries(f.countriesText) ?? []);
    return {
      name: f.name.trim(), subtitle: f.subtitle.trim(), description: f.description.trim(), keywords: f.keywords.trim(),
      supportUrl: f.supportUrl.trim(), marketingUrl: f.marketingUrl.trim(), promotionalText: f.promotionalText.trim(),
      copyright: f.copyright.trim(), primaryCategory: f.primaryCategory, secondaryCategory: f.secondaryCategory || null,
      noObjectionableContent: f.noObjectionableContent, usesThirdPartyContent: f.usesThirdPartyContent,
      traderStatusConfirmed: f.traderStatusConfirmed,
      countries: f.countries, distribution: f.distribution,
    };
  }

  // Before the two choices, when the typed country list was a field too:
  // setListingField(field: 'name' | 'subtitle' | 'description' | 'keywords' | 'supportUrl' | 'marketingUrl' | 'promotionalText' | 'copyright' | 'primaryCategory' | 'secondaryCategory' | 'countriesText', value: string): void {
  setListingField(field: 'name' | 'subtitle' | 'description' | 'keywords' | 'supportUrl' | 'marketingUrl' | 'promotionalText' | 'copyright' | 'primaryCategory' | 'secondaryCategory', value: string): void {
    this.listingForm = { ...this.listingForm, [field]: value };
    this.listingSaved = false;
    if (field === 'name') this.listingNameCheck.clear();
  }

  /** A name filled in for the builder never loses focus, so leaving the field alone would never check it. */
  private checkSeededListingName(): void {
    if (this.isProjectOwner) void this.listingNameCheck.checkIfNew(this.listingForm.name);
  }

  /** Someone who can't change the name has nothing to do with a warning about it. */
  leaveListingName(): void {
    if (this.isProjectOwner) void this.listingNameCheck.checkOnLeave(this.listingForm.name);
  }

  // Before the trader status tick:
  // setListingFlag(field: 'noObjectionableContent' | 'usesThirdPartyContent', value: boolean): void {
  setListingFlag(field: 'noObjectionableContent' | 'usesThirdPartyContent' | 'traderStatusConfirmed', value: boolean): void {
    this.listingForm = { ...this.listingForm, [field]: value };
    this.listingSaved = false;
  }

  // Before the two choices:
  // setListingChoice(patch: Partial<Pick<ListingFormView, 'countriesMode' | 'distribution'>>): void {
  setListingChoice(patch: Partial<Pick<ListingFormView, 'countries' | 'distribution'>>): void {
    this.listingForm = { ...this.listingForm, ...patch };
    this.listingSaved = false;
  }

  async saveListing(): Promise<void> {
    // Before a save could wait for a check:
    // if (!this.isProjectOwner || this.listingSaving) return;
    // A save during a check could change the choice the check is acting on.
    if (!this.isProjectOwner || this.listingSaving || this.euCheckBusy) return;
    this.listingSaved = false;
    const form = this.listingFormValue();
    // Every problem at once, before the round trip; the server stays the authority.
    this.listingErrors = clientListingErrors(form);
    if (this.listingErrors.length) return;
    this.listingSaving = true;
    try {
      const r = await this.membersService.saveListing(form);
      if (!r.ok) { this.listingErrors = [r.error || 'Could not save the listing.']; return; }
      if (r.state) { this.applyListingState(r.state, true); this.seedListingForm(); }
      this.listingSaved = true;
    } finally {
      this.listingSaving = false;
    }
  }

  trackChecklist = (_: number, item: AppStoreChecklistItem) => item.id;
  /** The thumbnails iterate `thumbUrls`, which line up with the stored screenshots by index. */
  trackThumb = (i: number, url: string) => this.listingStatus?.listing?.screenshots?.[i]?.path ?? url;

  checklistItem(id: string): AppStoreChecklistItem | undefined {
    return this.listingStatus?.checklist.find((i) => i.id === id);
  }

  async tickWebsiteStep(item: AppStoreChecklistItem, box: Pick<HTMLInputElement, 'checked'>): Promise<void> {
    const done = box.checked;
    if (!this.isProjectOwner || this.stepBusy) { box.checked = !done; return; }
    this.stepBusy = item.id; this.checklistError = null;
    try {
      const r = await this.membersService.setWebsiteStep(item.id as 'appPrivacy' | 'agreements' | 'unlisted', done);
      // The binding didn't change, so put the box back by hand when the server said no.
      if (!r.ok) { box.checked = !done; this.checklistError = r.error || 'Could not update the checklist.'; return; }
      if (r.state) this.applyListingState(r.state, true);
    } finally {
      this.stepBusy = null;
    }
  }

  screenshotCount(): number { return this.listingStatus?.listing?.screenshots?.length ?? 0; }

  /** Checked here for size and type first; the server checks again after the upload. */
  async onScreenshotsPicked(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length || !this.isProjectOwner || this.shotBusy) return;
    this.shotBusy = true; this.shotError = null;
    try {
      for (const file of files) {
        if (this.screenshotCount() >= this.maxScreenshots) { this.shotError = `The App Store takes at most ${this.maxScreenshots} screenshots. Remove one first.`; break; }
        if (file.type !== 'image/png') { this.shotError = 'Screenshots must be PNG files.'; break; }
        const size = await readImageSize(file).catch(() => null);
        if (!size || !isScreenshotSize(size.width, size.height)) { this.shotError = SCREENSHOT_SIZE_MESSAGE; break; }
        const u = await this.membersService.screenshotUploadUrl();
        if (!u.ok || !u.url || !u.path) { this.shotError = u.error || 'Could not get an upload link.'; break; }
        if (!(await this.membersService.uploadScreenshot(u.url, file))) { this.shotError = "The screenshot didn't upload. Try again."; break; }
        const r = await this.membersService.addScreenshot(u.path);
        if (!r.ok) { this.shotError = r.error || 'Could not add the screenshot.'; break; }
        if (r.state) this.applyListingState(r.state, true);
      }
    } finally {
      this.shotBusy = false;
    }
  }

  async removeScreenshotAt(index: number): Promise<void> {
    const path = this.listingStatus?.listing?.screenshots?.[index]?.path;
    if (!path || !this.isProjectOwner || this.shotBusy) return;
    this.shotBusy = true; this.shotError = null;
    try {
      const r = await this.membersService.removeScreenshot(path);
      if (!r.ok) { this.shotError = r.error || 'Could not remove the screenshot.'; return; }
      if (r.state) this.applyListingState(r.state, true);
    } finally {
      this.shotBusy = false;
    }
  }

  privacyApproved(): boolean { return !!this.listingStatus?.listing?.privacy?.approvedText; }

  private seedPrivacyText(): void {
    const p = this.listingStatus?.listing?.privacy;
    this.privacyText = p?.draft ?? p?.approvedText ?? '';
    this.privacyLinkText = p?.ownUrl ?? '';
  }

  /** The radio shown: what the owner just picked, else what is stored. */
  privacyChoice(): AppStorePrivacyChoice { return this.privacyPicked ?? privacyChoiceOf(this.listingStatus?.listing); }

  /** Apple's link is the own one only once it is stored; a link still being typed isn't in force. */
  ownPrivacyLinkInForce(): boolean { return privacyChoiceOf(this.listingStatus?.listing) === 'own'; }

  async choosePrivacy(target: AppStorePrivacyChoice): Promise<void> {
    if (!this.isProjectOwner || this.privacyBusy) return;
    this.privacyError = null;
    this.privacyPicked = target;
    const send = privacyChoiceToSend(target, this.listingStatus?.listing);
    if (!send) {
      if (target === privacyChoiceOf(this.listingStatus?.listing)) this.privacyPicked = null;
      return;
    }
    // A refused switch puts the radio back: nothing changed on the server.
    if (!(await this.sendPrivacyChoice(send))) { this.privacyPicked = null; return; }
    // An own link meant no draft was written on load, so the first switch to Zavo's policy writes it.
    if (target === 'zavo' && shouldDraftPrivacy(this.listingStatus?.listing)) await this.draftPrivacyPolicy(false);
    else if (target === 'zavo' && !this.privacyText) {
      const p = this.listingStatus?.listing?.privacy;
      this.privacyText = p?.draft ?? p?.approvedText ?? '';
    }
  }

  async saveOwnPrivacyLink(): Promise<void> {
    if (!this.isProjectOwner || this.privacyBusy) return;
    // Before the link went as the server stores it:
    // const ownUrl = this.privacyLinkText.trim();
    this.privacyError = privacyLinkError(this.privacyLinkText);
    const ownUrl = ownPrivacyLink(this.privacyLinkText);
    if (this.privacyError || !ownUrl) return;
    if (await this.sendPrivacyChoice({ choice: 'own', ownUrl })) this.privacyLinkText = ownUrl;
  }

  private async sendPrivacyChoice(input: AppStorePrivacyChoiceInput): Promise<boolean> {
    this.privacyBusy = true; this.privacyError = null;
    try {
      const r = await this.membersService.setPrivacyChoice(input);
      if (!r.ok) { this.privacyError = r.error || 'Could not save the privacy policy choice.'; return false; }
      if (r.state) this.applyListingState(r.state, true);
      this.privacyPicked = null;
      return true;
    } finally {
      this.privacyBusy = false;
    }
  }

  /** `reset` = "Reset to Zavo's draft"; false = the first fill, which keeps a draft already there. */
  async draftPrivacyPolicy(reset: boolean): Promise<void> {
    if (!this.isProjectOwner || this.privacyBusy) return;
    this.privacyBusy = true; this.privacyError = null;
    try {
      const r = await this.membersService.draftPrivacy(reset);
      if (!r.ok) { this.privacyError = r.error || 'Could not draft the privacy policy.'; return; }
      if (r.state) this.applyListingState(r.state, true);
      this.privacyText = r.draft ?? '';
    } finally {
      this.privacyBusy = false;
    }
  }

  async approvePrivacyPolicy(): Promise<void> {
    if (!this.isProjectOwner || this.privacyBusy) return;
    const text = this.privacyText.trim();
    if (!text) { this.privacyError = 'Write the privacy policy first.'; return; }
    this.privacyBusy = true; this.privacyError = null;
    try {
      const r = await this.membersService.approvePrivacy(text);
      if (!r.ok) { this.privacyError = r.error || 'Could not approve the privacy policy.'; return; }
      if (r.state) this.applyListingState(r.state, true);
      this.privacyEditing = false;
      this.seedPrivacyText();
    } finally {
      this.privacyBusy = false;
    }
  }

  editPrivacyPolicy(): void {
    const p = this.listingStatus?.listing?.privacy;
    this.privacyText = p?.approvedText ?? p?.draft ?? '';
    this.privacyEditing = true; this.privacyError = null;
  }

  /** The owner's notes box starts from what is saved; a poll never overwrites what they are typing. */
  private seedRejectionNotes(): void {
    this.rejectionNotesText = this.listingStatus?.listing?.rejectionNotes?.text ?? '';
  }

  setRejectionNotesText(value: string): void {
    this.rejectionNotesText = value;
    this.rejectionNotesSaved = false;
    this.rejectionNotesError = null;
  }

  /** OWNER: saves Apple's notes on the rejected version; empty text clears them. The server enforces the same rules. */
  async saveRejectionNotes(): Promise<void> {
    if (!this.isProjectOwner || this.rejectionNotesBusy || !rejectedPauseShown(this.listingStatus?.submission)) return;
    this.rejectionNotesSaved = false;
    const input = rejectionNotesInput(this.rejectionNotesText);
    if (!input.ok) { this.rejectionNotesError = input.error; return; }
    this.rejectionNotesBusy = true; this.rejectionNotesError = null;
    try {
      const r = await this.membersService.saveRejectionNotes(input.text);
      if (!r.ok) { this.rejectionNotesError = r.error || "Could not save Apple's notes."; return; }
      if (r.state) this.applyListingState(r.state, true);
      this.rejectionNotesText = input.text;
      this.rejectionNotesSaved = true;
    } finally {
      this.rejectionNotesBusy = false;
    }
  }

  /** App Store Connect's page for this app, for a failed or rejected submission. */
  appStoreConnectAppUrl(): string {
    return `${this.appStoreConnectUrl}/${this.appleAccount?.appleAppId ?? ''}/distribution`;
  }

  /** `alreadyConfirmed`: the builder has just clicked a card that said the same thing as this prompt. */
  async submitToAppStore(alreadyConfirmed = false): Promise<void> {
    const s = this.listingStatus;
    if (!this.isProjectOwner || this.submitting || !s?.canSubmit) return;
    // The first time only: this puts the client's business name in front of Apple as the seller.
    if (!s.submission && !alreadyConfirmed) {
      const name = s.listing?.name || this.mobileAppName || 'this app';
      if (!confirm(`Submit ${name} to Apple's App Store review? Your client's business name appears as the seller.`)) return;
    }
    this.submitting = true; this.submitError = null;
    try {
      const r = await this.membersService.submitToAppStore();
      if (!r.ok) {
        // A refusal that carries the state (agreement, reviewer login) turns the row to fix red/todo.
        if (r.state) this.applyListingState(r.state, true);
        this.submitError = r.error || 'Could not submit to the App Store.';
        return;
      }
      // A refusal from Apple comes back ok with submission.status 'failed' and its error, shown on the status line.
      if (r.state) this.applyListingState(r.state, true);
    } finally {
      this.submitting = false;
    }
  }

  /** Switch the editor's data view to a tenant (null = Default (Editor)). Owner-only UI;
   *  the gateway re-validates server-side. Reload so every page refetches under the new scope. */
  selectTenant(tenantId: string | null): void {
    this.clientService.selectedTenantId = tenantId;
  }

  closeSettings(): void {
    this.settingsCard = null;
    this.showSettings = false; this.stopLiveApkPoll(); this.stopIosPoll(); this.stopListingPoll(); this.stopDevApkWatch();
    this.rebuildOpen = false;
    // The key and the reviewer's password must not sit in memory once the panel is gone.
    this.appleForm.p8 = ''; this.appleP8Name = ''; this.reviewForm.demoPassword = '';
    this.runtimePasswordPrefill = '';
    for (const commit of SETTINGS_PRESS_COMMITS) this.waits.report(commit, { outcome: 'closed', because: 'the builder closed Settings' });
  }

  /** Which form holds something the builder would lose if Settings closed or Claude filled it again. */
  settingsFormInUse(): string | null {
    if (this.unpublishOpen && this.unpublishName) return 'The Unpublish form';
    if (this.rebuildOpen && (this.rebuildName || this.rebuildNote)) return 'The Rebuild form';
    if (this.timeZonePending) return 'The time zone change';
    // Closing keeps what was typed and drops only the key, so only the key can be lost.
    if (this.appleForm.p8) return 'The Apple connect form';
    if (this.runtimePasswordPrefill) return 'The live app password form';
    return null;
  }

  copyCode(): void {
    if (!this.builderCode) return;
    navigator.clipboard?.writeText(this.builderCode);
    this.codeCopied = true;
    setTimeout(() => (this.codeCopied = false), 1500);
  }

  // ---- Owner's runtime test login ----
  /** Reveal the owner's runtime login. Generates + stores a unique password on first call. */
  async revealRuntimeLogin(): Promise<void> {
    this.runtimeLoginLoading = true;
    this.runtimeLoginError = '';
    const project = this.settingsProjectId();
    const login = await this.membersService.getRuntimeLogin();
    if (this.settingsProjectId() !== project) { this.runtimeLoginLoading = false; return; }
    if (login) { this.runtimeLoginEmail = login.email; this.runtimeLoginPassword = login.password; this.runtimeLoginShown = true; }
    else this.runtimeLoginError = 'Could not load the runtime login.';
    this.runtimeLoginLoading = false;
  }

  /** Set a custom runtime password (≥6 chars), or regenerate a random one when the field is empty. */
  async updateRuntimePassword(): Promise<void> {
    const custom = this.runtimeNewPassword.trim();
    if (custom && custom.length < 6) {
      this.runtimeLoginError = 'Password must be at least 6 characters.';
      this.waits.report('set-runtime-test-password', { outcome: 'failed', reason: this.runtimeLoginError });
      return;
    }
    this.runtimeLoginLoading = true;
    this.runtimeLoginError = '';
    this.waits.saving('set-runtime-test-password');
    const project = this.settingsProjectId();
    const r = await this.membersService.setRuntimePassword(custom || undefined);
    if (this.settingsProjectId() !== project) { this.runtimeLoginLoading = false; return; }
    if (r.ok && r.login) {
      this.runtimeLoginEmail = r.login.email; this.runtimeLoginPassword = r.login.password; this.runtimeNewPassword = ''; this.runtimeLoginShown = true;
      this.waits.report('set-runtime-test-password', { outcome: 'saved', saved: { passwordChanged: true } });
    } else {
      this.runtimeLoginError = r.error || 'Could not change the password.';
      this.waits.report('set-runtime-test-password', { outcome: 'failed', reason: this.runtimeLoginError });
    }
    this.runtimeLoginLoading = false;
  }

  copyRuntimeLogin(): void {
    if (!this.runtimeLoginPassword) return;
    navigator.clipboard?.writeText(`${this.runtimeLoginEmail} / ${this.runtimeLoginPassword}`);
    this.runtimeLoginCopied = true;
    setTimeout(() => (this.runtimeLoginCopied = false), 1500);
  }

  ngOnInit() {
    // Detect initial route
    this.detectCurrentRoute(this.router.url);

    // Listen for route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.detectCurrentRoute(event.url);
    });

    // Populate the profile from the Firebase user directly. The old code read authService.user
    // inside an isAuthenticated() emission — a race that left userName stuck on "Loading…" whenever
    // the user object wasn't set yet at that instant. user() emits the resolved User, so this is
    // reliable and self-corrects when auth settles.
    user(this.auth).subscribe((u) => {
      if (!u) { this.canSeeBastion = false; return; }
      // Read only once a user has resolved: the claim check answers false while currentUser is still unset.
      this.authService.hasZavoTeamClaim().then(
        (onTeam) => { this.canSeeBastion = onTeam; },
        () => { this.canSeeBastion = false; },
      );
      this.userName = u.displayName || u.email?.split('@')[0] || 'User';
      this.userEmail = u.email || '';
      this.userInitials = u.displayName
        ? this.generateInitials(u.displayName)
        : (u.email?.charAt(0).toUpperCase() || '--');
    });
    
    this.selectedClientId = this.clientService.selectedClientId;

    // Eager, not lazy on settings-open — see `canSeeUsersWorkspaces`'s comment. And re-run on every
    // `clientChanged$`, not just once here: `LeftNavComponent` lives in `LayoutComponent`, the PARENT
    // of client-list/entity-list/pages/users-workspaces, so its `ngOnInit` runs exactly once — right
    // there, before any project is picked. Picking a project (or switching between two) is an
    // in-layout `router.navigate`, which never re-creates this component, so without the
    // subscription this call below is the ONLY time the gate is ever computed: it reads no project
    // (item hidden for everyone) and then never runs again, even for an owner who later switches to
    // a project where they are a viewer. The initial call still matters for the case where a project
    // is already selected when the nav mounts (e.g. a hard reload while inside a project).
    this.loadUsersWorkspacesAccess();
    this.usersWorkspacesAccessSub = this.authService.clientChanged$.subscribe(() => {
      this.loadUsersWorkspacesAccess();
      this.forgetProjectSettings();
      if (this.showSettings) void this.openSettings();
    });
    this.screenPanels.settings = this.settingsOverlay;

    this.navSub = this.nav.intent$.subscribe((i) => { if (i?.kind === 'settings') { this.nav.take('settings'); void this.openSettings(); } });

    this.schedulePulse();
  }

  /** See the call site's comment in `ngOnInit` for why this must run on every `clientChanged$`. */
  private async loadUsersWorkspacesAccess(): Promise<void> {
    const projectId = this.clientService.selectedClient?.id || this.clientService.selectedClientId || '';
    if (!projectId) { this.canSeeUsersWorkspaces = false; void this.loadIosNudge('', false); return; }
    try {
      const level = await accessLevelFor(projectId, () => this.membersService.myAccessLevel());
      if ((this.clientService.selectedClient?.id || this.clientService.selectedClientId || '') !== projectId) return;   // switched meanwhile: the newer call decides
      this.canSeeUsersWorkspaces = level === 'owner' || level === 'editor';
      void this.loadIosNudge(projectId, level === 'owner');
    } catch {
      // A failed fetch (e.g. a token refresh that rejected) must not read as "confirmed viewer" —
      // fail closed by hiding the item for now rather than guessing, but the failure is NOT cached
      // (see access-level-cache.util.ts): the next clientChanged$ emission, or a fresh mount, retries.
      this.canSeeUsersWorkspaces = false;
      void this.loadIosNudge(projectId, false);
    }
  }

  ngOnDestroy() {
    if (this.screenPanels.settings === this.settingsOverlay) this.screenPanels.settings = null;
    clearTimeout(this.pulseTimer);
    this.stopLiveApkPoll();
    this.stopIosPoll();
    this.stopListingPoll();
    this.stopDevApkWatch();
    this.usersWorkspacesAccessSub?.unsubscribe();
    this.navSub?.unsubscribe();
  }

  private schedulePulse() {
    const delay = (10 + Math.random() * 10) * 1000;
    this.pulseTimer = setTimeout(() => {
      const panel = this.el.nativeElement.querySelector('.nav-panel');
      if (panel) {
        panel.classList.add('pulse-active');
        setTimeout(() => panel.classList.remove('pulse-active'), 2000);
      }
      this.schedulePulse();
    }, delay);
  }

  /**
   * Generate initials from name
   */
  private generateInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * Determines the current route category based on URL
   */
  private detectCurrentRoute(url: string): void {
    if (url.includes('/bastion')) {
      this.currentRoute = 'bastion';
    } else if (url.includes('entity-list') || url.includes('entity-detail')) {
      this.currentRoute = 'entities';
    } else if (url.includes('pages')) {
      this.currentRoute = 'pages';
    } else if (url.includes('users-workspaces')) {
      this.currentRoute = 'usersworkspaces';
    } else if (url.includes('user-mgmt')) {
      this.currentRoute = 'userandroles';
    } else if (url.includes('modules')) {
      this.currentRoute = 'modules';
    } else if (url.includes('client-list')) {
      this.currentRoute = 'clients';
    } else if (url.includes('query')) {
      this.currentRoute = 'aiquery';
    }
  }

  /**
   * Navigate to Entities page
   */
  navigateToEntites() {
    this.currentRoute = 'entities';
    this.router.navigate(['/dashboard/entity-list']);
  }

  /**
   * Navigate to Pages section
   */
  navigateToPages() {
    if (!this.nav.confirmLeave()) return;
    this.currentRoute = 'pages';
    this.router.navigate(['/dashboard/pages']);
  }

  /**
   * Navigate to Bastion — the flow list
   */
  navigateToBastion() {
    if (!this.nav.confirmLeave()) return;
    this.currentRoute = 'bastion';
    this.router.navigate(['/dashboard/bastion']);
  }

  openConnectClaude() {
    getZavoBridge()?.openConnectPanel?.();
  }

  /**
   * Navigate to User Management section
   */
  navigateToUserandRoles() {
    if (!this.nav.confirmLeave()) return;
    this.currentRoute = 'userandroles';
    this.router.navigate(['/dashboard/user-mgmt']);
  }

  navigateToUsersWorkspaces() {
    if (!this.nav.confirmLeave()) return;
    this.currentRoute = 'usersworkspaces';
    this.router.navigate(['/dashboard/users-workspaces']);
  }

  /**
   * Navigate to Modules section
   */
  navigateToModules() {
    this.currentRoute = 'modules';
    this.router.navigate(['/dashboard/modules']);
  }

  /**
   * Navigate to Client List to select a new client
   */
  navigateToClientList() {
    if (!this.nav.confirmLeave()) return;
    // Nav 7b (spec Q8, R4) — Switch Project forgets the remembered project, or the reload below would restore it.
    sessionStorage.removeItem(PROJECT_KEY);
    window.location.reload();
  }

  /**
   * Navigate to Settings (placeholder)
   */
  navigateToSettings() {
    // Placeholder for future implementation
    console.log('Settings navigation not yet implemented');
  }

  /**
   * Navigate to Help & Support (placeholder)
   */
  navigateToHelp() {
    // Placeholder for future implementation
    console.log('Help navigation not yet implemented');
  }

  /**
   * Navigate to AI Query section
   */
  navigateToAIQuery() {
    this.currentRoute = 'aiquery';
    this.router.navigate(['/query']);
  }

  /**
   * Toggle user profile dropdown
   */
  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  /**
   * Sign out the current user
   */
  signOut() {
    if (!this.nav.confirmLeave()) return;
    this.authService.signOut().then(() => {
      // The login page is the root route (''), there is no '/login' route — navigating to
      // '/login' silently failed, which is why sign-out used to require a manual refresh.
      this.router.navigate(['/']);
    }).catch(error => {
      console.error('Error signing out:', error);
    });
  }

  /**
   * Refresh the selected client ID
   */
  refreshClientId() {
    this.selectedClientId = this.clientService.selectedClientId;
  }
}
